"use client"

import { useState, useEffect } from "react"
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2, AlertTriangle, CheckCircle, FileDown } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import { Button } from "@/components/ui/button"
import { supabase } from "@/utils/supabase/client"
import { useAuth } from "@/contexts/auth-context"
import { startOfMonth, format } from "date-fns"

interface BudgetData {
  category: string;
  budget: number;
  actual: number;
  variance: number;
}

export function ExpenseBudget() {
  const [data, setData] = useState<BudgetData[] | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()
  const { currentOrganization } = useAuth()

  useEffect(() => {
    const fetchData = async () => {
      if(!currentOrganization) return;
      setIsLoading(true);

      try {
        const firstDayOfMonth = format(startOfMonth(new Date()), 'yyyy-MM-dd');

        const { data: categories, error: catError } = await supabase
            .from('expense_categories')
            .select('id, name, budget_amount')
            .eq('organization_id', currentOrganization.id);
        
        if (catError) throw catError;

        const { data: expenses, error: expError } = await supabase
            .from('expenses')
            .select('category_id, amount')
            .eq('organization_id', currentOrganization.id)
            .gte('expense_date', firstDayOfMonth);
        
        if (expError) throw expError;

        const actuals = expenses.reduce((acc, e) => {
            if (e.category_id) {
                acc[e.category_id] = (acc[e.category_id] || 0) + e.amount;
            }
            return acc;
        }, {} as Record<string, number>);

        const budgetData = categories.map(cat => {
            const actual = actuals[cat.id] || 0;
            const budget = cat.budget_amount || 0;
            return {
                category: cat.name,
                budget,
                actual,
                variance: budget - actual,
            };
        });

        setData(budgetData);

      } catch (error: any) {
        toast({
          title: "Error",
          description: "Could not load budget data. " + error.message,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [toast, currentOrganization]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!data) {
    return <p className="text-center text-muted-foreground mt-8">Could not load budget data.</p>
  }

  const budgetAlerts = data.filter(d => d.variance < 0);
  const totalBudget = data.reduce((sum, d) => sum + d.budget, 0);
  const totalActual = data.reduce((sum, d) => sum + d.actual, 0);

  return (
    <div className="space-y-6 mt-6">
       <div className="grid gap-6 lg:grid-cols-3">
          {/* Budget vs Actual Chart */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Budget vs Actual</CardTitle>
              <Button variant="outline" size="sm"><FileDown className="mr-2 h-4 w-4"/> Export</Button>
            </CardHeader>
            <CardContent>
                 <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={data} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="category" stroke="#888888" fontSize={12} />
                        <YAxis stroke="#888888" fontSize={12} tickFormatter={(value) => `Rs.${value/1000}k`} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="budget" fill="#8884d8" name="Budget" />
                        <Bar dataKey="actual" fill="#82ca9d" name="Actual Spending" />
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
        
        {/* Alerts and Summary */}
        <div className="space-y-6">
            <Card>
                <CardHeader><CardTitle>Budget Alerts</CardTitle></CardHeader>
                <CardContent>
                    {budgetAlerts.length === 0 ? (
                        <div className="flex items-center text-green-600">
                            <CheckCircle className="h-5 w-5 mr-2"/>
                            <p>All budgets are within limits.</p>
                        </div>
                    ) : (
                        <ul className="space-y-2">
                            {budgetAlerts.map(alert => (
                                <li key={alert.category} className="flex items-start text-destructive">
                                    <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0 mt-1"/>
                                    <div>
                                        <span className="font-semibold">{alert.category}</span> is over budget by Rs. {Math.abs(alert.variance).toLocaleString()}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
             <Card>
                <CardHeader><CardTitle>Budget Summary</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Total Budget</span>
                        <span className="font-semibold">Rs. {totalBudget.toLocaleString()}</span>
                    </div>
                     <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Total Spending</span>
                        <span className="font-semibold">Rs. {totalActual.toLocaleString()}</span>
                    </div>
                     <div className="flex justify-between items-center border-t pt-2">
                        <span className="text-muted-foreground">Remaining</span>
                        <span className={`font-bold ${totalBudget - totalActual < 0 ? 'text-destructive' : 'text-green-600'}`}>
                            Rs. {(totalBudget - totalActual).toLocaleString()}
                        </span>
                    </div>
                </CardContent>
            </Card>
        </div>
       </div>
    </div>
  )
} 